import * as React from 'react'
import { Box, Tooltip, Badge } from 'cs-kevlar-react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  A400,
  SL400,
  EL400,
  N1300,
  N80,
} from 'cs-kevlar-react/lib/es/theming/palette'
import { InterfaceHealthColor } from '../api'
import { useCustomers } from 'hooks'
import clsx from 'clsx'
import { ensureHealthColorIsEnumValue } from 'utils/health'

interface ConnectorHealthBadgeProps {
  health?: InterfaceHealthColor
  hideStatuses?: InterfaceHealthColor[]
  className?: string
  enableUnknownStatus?: boolean
}

const useStyles = makeStyles(theme =>
  createStyles({
    badge: {
      '& .MuiBadge-badge': {
        height: 8,
        width: 8,
        minWidth: 8,
        minHeight: 8,
        padding: 0,
      },
    },
    badgeGreen: {
      '& .MuiBadge-badge': {
        backgroundColor: SL400,
      },
    },
    badgeYellow: {
      '& .MuiBadge-badge': {
        backgroundColor: A400,
      },
    },
    badgeRed: {
      '& .MuiBadge-badge': {
        backgroundColor: EL400,
      },
    },
    badgeOrange: {
      '& .MuiBadge-badge': {
        backgroundColor: A400,
      },
    },
    badgeBlack: {
      '& .MuiBadge-badge': {
        backgroundColor: N1300,
      },
    },
    badgeLoading: {
      '& .MuiBadge-badge': {
        backgroundColor: 'transparent',
        border: `1px solid ${N80}`,
      },
    },
  }),
)

type InterfaceHealthTooltips = {
  [P in number]: string
}

const LABELS: InterfaceHealthTooltips = {
  [InterfaceHealthColor.Black]: 'Offline',
  [InterfaceHealthColor.Red]: 'Error',
  [InterfaceHealthColor.Yellow]: 'Warning',
  [InterfaceHealthColor.Orange]: 'Warning',
  [InterfaceHealthColor.Green]: 'Connected',
}

const classResolver = (health: InterfaceHealthColor, classes: any) => {
  // health could be a numeric value, or a color value like Green, Black, Red, etc...
  // map either of those to the enum value
  const mappedEnumValue = ensureHealthColorIsEnumValue(health)

  if (mappedEnumValue === InterfaceHealthColor.Red) {
    return classes.badgeRed
  }

  if (mappedEnumValue === InterfaceHealthColor.Yellow) {
    return classes.badgeYellow
  }

  if (mappedEnumValue === InterfaceHealthColor.Green) {
    return classes.badgeGreen
  }

  if (mappedEnumValue === InterfaceHealthColor.Orange) {
    return classes.badgeOrange
  }

  return classes.badgeBlack
}

type WithLabels = {
  TooltipLabels: InterfaceHealthTooltips
}

export const ConnectorHealthBadge: React.FunctionComponent<
  ConnectorHealthBadgeProps
> &
  WithLabels = ({ health, hideStatuses, className, enableUnknownStatus }) => {
  const { customersContext } = useCustomers()

  const classes = useStyles()

  if (enableUnknownStatus && !customersContext.hasLoadedHealth) {
    return (
      <Tooltip
        placement="top"
        arrow
        title="Loading connector health"
        aria-label="Loading connector health"
      >
        <Badge
          className={clsx(classes.badge, classes.badgeLoading, className)}
          badgeContent=""
          color="primary"
        >
          <Box />
        </Badge>
      </Tooltip>
    )
  }

  const defaultedHealth = ensureHealthColorIsEnumValue(
    health ?? InterfaceHealthColor.Black,
  )

  if (hideStatuses && hideStatuses.includes(defaultedHealth)) {
    return <Box></Box>
  }

  return (
    <Tooltip
      placement="top"
      arrow
      title={LABELS[defaultedHealth] ?? 'Down'}
      aria-label={LABELS[defaultedHealth]}
    >
      <Badge
        className={clsx(
          classes.badge,
          classResolver(defaultedHealth, classes),
          className,
        )}
        badgeContent=""
        color="primary"
      >
        <Box />
      </Badge>
    </Tooltip>
  )
}

ConnectorHealthBadge.TooltipLabels = LABELS
